import { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import {
  BackArrowIcon,
  CircleWithTickIcon
} from "../../front-end-global-components/assets/assets";
import errorIcon from "../../front-end-global-components/assets/error.svg";
import Button from "../../front-end-global-components/components/Button/Button";
import CropperModal from "../../front-end-global-components/components/CropperModal/CropperModal";
import FeatureFlagsFallback from "../../front-end-global-components/components/FeatureFlagsFallback/FeatureFlagsFallback";
import Header from "../../front-end-global-components/components/Header/Header";
import InputBox from "../../front-end-global-components/components/InputBox/InputBox";
import Modal from "../../front-end-global-components/components/Modal/Modal";
import NativeSelect from "../../front-end-global-components/components/NativeSelect/NativeSelect";
import OTPInputBox from "../../front-end-global-components/components/OTPInputBox/OTPInputBox";
import RoundedProfilePicture from "../../front-end-global-components/components/RoundedProfilePicture/RoundedProfilePicture";
import TextArea from "../../front-end-global-components/components/TextArea/TextArea";
import { logout } from "../../redux/authentication/actions";
import {
  editProfile,
  generateOtpForUpdate,
  saveEditProfile,
  setTransactionId,
  verifyOtpForUpdate
} from "../../redux/patients/actions";
import { setErrorStatus } from "../../redux/status/actions";
import {
  stateCodeWithDistricts,
  stateOptionsWithCode
} from "../../utils/constants";
import { editProfileSchema } from "../../utils/models";
import { Regex } from "../../utils/regex";
import { isValidObject, validation } from "../../utils/validators";

const EditProfile = (props) => {
  const [profileData, setProfileData] = useState(null);
  const [error, setError] = useState({});
  const [mode, setMode] = useState(null);
  const [counter, setCounter] = useState(null);
  const [running, setRunning] = useState(false);
  const [formData, setFormData] = useState({
    profilePhoto: "",
    name: {
      first: "",
      middle: "",
      last: ""
    },
    gender: "",
    email: "",
    phoneNumber: "+91",
    dateOfBirth: "",
    address: "",
    stateCode: "",
    districtCode: "",
    pinCode: ""
  });
  const [file, setFile] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [resendCount, setResendCount] = useState(0);
  const [contactValue, setContactValue] = useState(null);
  const contactValueRef = useRef(null);
  const modeRef = useRef(null);

  useEffect(() => {
    let counterTimer;
    if (counter && typeof counter === "number" && running) {
      counterTimer = setTimeout(() => {
        setCounter(counter - 1);
      }, 1000);
      return;
    }
    setCounter(null);
    setRunning(false);
    return () => {
      clearTimeout(counterTimer);
    };

    // eslint-disable-next-line
  }, [counter]);

  useEffect(() => {
    const { currentProfile, healthIdData } = props.patients;
    const profileData = healthIdData?.[currentProfile];

    if (isValidObject(profileData)) {
      setProfileData(profileData);
    }
    // eslint-disable-next-line
  }, [
    props.patients.currentProfile,
    props.patients.healthIdData?.[props.patients.currentProfile]
  ]);

  const getGender = (gender) => {
    if (gender === "M") {
      return "male";
    } else if (gender === "F") {
      return "female";
    } else if (gender === "O") {
      return "others";
    } else if (gender === "U") {
      return "unknown";
    }
  };

  const combineDateOfBirth = () => {
    const date = `0${profileData?.dayOfBirth}`.slice(-2);
    const month = `0${profileData?.monthOfBirth}`.slice(-2);
    const year = profileData?.yearOfBirth;
    return `${year}-${month}-${date}`;
  };

  const getChangedFields = () => {
    const {
      name: { first, middle, last } = {},
      gender,
      dateOfBirth,
      address,
      stateCode,
      districtCode,
      pinCode,
      profilePhoto,
      email,
      phoneNumber
    } = formData || {};

    const {
      firstName: pFirst,
      middleName: pMiddle,
      lastName: pLast,
      gender: pGender,
      address: pAddress,
      stateCode: pStateCode,
      districtCode: pDistrictCode,
      pinCode: pPinCode,
      profilePhoto: pProfilePhoto,
      email: pEmail,
      mobile: pMobile
    } = profileData || {};

    const normalizedPMobile = pMobile?.includes("+91")
      ? pMobile
      : `+91${pMobile}`;
    const hasPhoneChange = !!(
      phoneNumber &&
      pMobile &&
      phoneNumber !== normalizedPMobile &&
      Regex.phoneNumber.test(phoneNumber)
    );

    const hasEmailChange = !!(
      (email && pEmail && email !== pEmail && Regex.email.test(email)) ||
      (email && !pEmail && Regex.email.test(email))
    );

    const hasProfileChanges = [
      (first || "") !== (pFirst || ""),
      (middle || "") !== (pMiddle || ""),
      (last || "") !== (pLast || ""),
      (gender || "") !== getGender(pGender || ""),
      (dateOfBirth || "") !== (combineDateOfBirth() || ""),
      (address || "") !== (pAddress || ""),
      (stateCode || "") !== (pStateCode || ""),
      (districtCode || "") !== (pDistrictCode || ""),
      (pinCode || "") !== (pPinCode || ""),
      (profilePhoto || "") !==
        (pProfilePhoto ? `data:image/png;base64, ${pProfilePhoto}` : "")
    ].some(Boolean);

    return { hasProfileChanges, hasPhoneChange, hasEmailChange };
  };

  useEffect(() => {
    if (profileData) {
      const data = {
        profilePhoto: profileData?.profilePhoto
          ? `data:image/png;base64, ${profileData?.profilePhoto}`
          : "",
        name: {
          first: profileData?.firstName || "",
          middle: profileData?.middleName || "",
          last: profileData?.lastName || ""
        },
        gender: getGender(profileData?.gender),
        email:
          contactValueRef.current && modeRef.current === "email"
            ? contactValueRef.current
            : profileData?.email,
        phoneNumber:
          contactValueRef.current && modeRef.current === "phoneNumber"
            ? contactValueRef.current
            : profileData?.mobile.includes("+91")
            ? profileData?.mobile
            : `+91${profileData?.mobile}`,
        dateOfBirth: combineDateOfBirth(),
        address: profileData?.address,
        stateCode: profileData?.stateCode,
        districtCode: profileData?.districtCode,
        pinCode: profileData?.pinCode
      };
      setFormData(data);
    }

    // eslint-disable-next-line
  }, [profileData]);

  function onChangeHandler(event) {
    if (event.target.name === "editImage") {
      return;
    }
    switch (event.target.name) {
      case "firstName":
        setFormData({
          ...formData,
          name: { ...formData?.name, first: event.target.value }
        });
        break;

      case "middleName":
        setFormData({
          ...formData,
          name: { ...formData?.name, middle: event.target.value }
        });
        break;

      case "lastName":
        setFormData({
          ...formData,
          name: { ...formData?.name, last: event.target.value }
        });
        break;

      default:
        setFormData({ ...formData, [event.target.name]: event.target.value });
        break;
    }
  }

  const handleDisable = () => {
    if (error) return false;
    const { hasProfileChanges, hasPhoneChange, hasEmailChange } =
      getChangedFields();
    return !!(hasProfileChanges || hasPhoneChange || hasEmailChange);
  };

  useEffect(() => {
    const transactionId = props.patients?.transaction?.id;
    const transactionValue = props.patients?.transaction?.phoneNumber;
    if (
      transactionId &&
      typeof transactionId === "string" &&
      !running &&
      !location.pathname.endsWith("/confirmLogin") &&
      resendCount === 0
    ) {
      if (transactionValue) {
        const derivedMode = Regex.phoneNumber.test(transactionValue)
          ? "phoneNumber"
          : "email";
        setMode(derivedMode);
        setContactValue(transactionValue);
        modeRef.current = derivedMode;
        contactValueRef.current = transactionValue;
      }
      setCounter(60);
      setRunning(true);
      navigate("verify");
      return;
    }
    setCounter(null);
    setRunning(false);

    if (!transactionId && location.pathname.endsWith("/verify")) {
      contactValueRef.current = null;
      modeRef.current = null;
      navigate(-1);
    }
    // eslint-disable-next-line
  }, [props.patients?.transaction?.id]);

  useEffect(() => {
    setTransactionId(null);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const errorFields = [];

    const optionalFields = ["middle", "email", "profilePhoto"];

    const validateField = (key, value, parentKey = "") => {
      const fieldName = parentKey ? `${parentKey}.${key}` : key;

      if (
        optionalFields.includes(key) &&
        (value === undefined || value === "" || value === null)
      ) {
        return;
      }

      if (value && typeof value === "object" && !Array.isArray(value)) {
        for (const [k, v] of Object.entries(value)) {
          validateField(k, v, fieldName);
        }
      } else {
        const validate = validation(key, value);
        if (validate.status === false) {
          errorFields.push(fieldName);
        }
      }
    };

    for (const [key, value] of Object.entries(formData)) {
      validateField(key, value);
    }

    if (!formData.name?.first?.trim()) {
      errorFields.push("name.first");
    }

    if (errorFields.length > 0) {
      setError(true);
    } else {
      setError(false);
    }
  }, [formData]);

  useEffect(() => {
    if (file) {
      navigate("cropper");
    }
    // eslint-disable-next-line
  }, [file]);

  return (
    <>
      <Header
        supportIconOnClick={() => {
          navigate("/query");
        }}
        title="Edit Profile"
        backButtonOnClick={() => {
          navigate("/settings");
          if (props.patients?.transaction?.id !== null) {
            setTransactionId(null);
          }
        }}
        incident={props.app.featureFlags?.incident?.patients}
        incidentOnClick={() => {
          navigate("/incidents");
        }}
      />
      <section className="remaining-body-height flex-justify-content-center padding-larger inherit-parent-width">
        {!props.app.featureFlags?.abhaService && <FeatureFlagsFallback />}

        {props.app.featureFlags?.abhaService && (
          <form
            className="inherit-parent-height max-width-588px display-flex  flex-direction-column flex-justify-content-space-between"
            onChange={(event) => {
              onChangeHandler(event);
            }}
            onSubmit={(event) => {
              event.preventDefault();
              if (location.pathname.includes("cropper")) return;
              if (!(isValidObject(formData) && isValidObject(profileData))) {
                return;
              }

              const { hasProfileChanges, hasPhoneChange, hasEmailChange } =
                getChangedFields();

              // Set refs before dispatching so the profileData useEffect does not
              // reset these fields while the saga refreshes profile data.
              if (hasPhoneChange) {
                modeRef.current = "phoneNumber";
                contactValueRef.current = formData.phoneNumber;
              } else if (hasEmailChange) {
                modeRef.current = "email";
                contactValueRef.current = formData.email;
              }

              saveEditProfile({
                profileData: editProfileSchema({ ...formData }),
                phoneNumber: formData.phoneNumber,
                email: formData.email,
                hasProfileChanges,
                hasPhoneChange,
                hasEmailChange
              });
            }}
          >
            <div className="flex-justify-content-center">
              <RoundedProfilePicture
                data-cy={"edit-profile-pic"}
                size="large"
                uploadFromAll={true}
                editMode={profileData?.kycStatus !== "VERIFIED"}
                setFile={(file) => setFile(file)}
                src={formData?.profilePhoto || null}
              />
            </div>
            <div className="flex-justify-content-center font-size-medium flex-direction-column padding-top-large flex-align-items-center">
              <div className="display-flex">
                {profileData?.kycStatus === "VERIFIED" ? (
                  <>
                    <div className="padding-right-default">KYC Verified</div>
                    <CircleWithTickIcon
                      circleColor={"white"}
                      circleOutline={"#00a000"}
                      tickColor={"#00a000"}
                    />
                  </>
                ) : (
                  <>
                    <div className="padding-right-default">Self Declared</div>
                    <img src={errorIcon} alt="" />
                  </>
                )}
              </div>
              <div className="padding-top-default">{`${profileData?.abhaAddress}`}</div>
              {profileData?.kycStatus === "VERIFIED" && (
                <div className="padding-top-default padding-bottom-default">{`${profileData?.abhaNumber}`}</div>
              )}
            </div>
            <div className="overflow-y-auto">
              <InputBox
                labelClassName="letter-spacing-4-percentage"
                className="inherit-parent-width border-radius-default"
                label="first name"
                type="text"
                name="firstName"
                data-cy="FIRST-NAME-INPUT"
                disabled={profileData?.kycStatus === "VERIFIED"}
                value={formData?.name.first}
                validation={(value) => validation("firstName", value)}
              />
              <InputBox
                labelClassName="letter-spacing-4-percentage"
                className="inherit-parent-width border-radius-default"
                label="middle name"
                type="text"
                name="middleName"
                data-cy="MIDDLE-NAME-INPUT"
                disabled={profileData?.kycStatus === "VERIFIED"}
                value={formData?.name.middle}
                validation={(value) => validation("middleName", value)}
              />

              <InputBox
                labelClassName="letter-spacing-4-percentage"
                className="inherit-parent-width border-radius-default"
                label="last name"
                type="text"
                name="lastName"
                disabled={profileData?.kycStatus === "VERIFIED"}
                data-cy="LAST-NAME-INPUT"
                value={formData?.name.last}
                validation={(value) => validation("lastName", value)}
              />

              <InputBox
                labelClassName="letter-spacing-4-percentage"
                className="inherit-parent-width border-radius-default"
                label="Phone Number"
                type="tel"
                name="phoneNumber"
                value={formData?.phoneNumber}
                validation={(value) => validation("phoneNumber", value)}
                data-cy="PHONE-NUMBER-INPUT"
              />

              <div className="display-flex flex-center-children-vertically">
                <InputBox
                  labelClassName="letter-spacing-4-percentage"
                  className="inherit-parent-width border-radius-default padding-right-default"
                  label="Date Of Birth"
                  type="date"
                  disabled={profileData?.kycStatus === "VERIFIED"}
                  size="half"
                  name="dateOfBirth"
                  value={formData?.dateOfBirth}
                  validation={(value) => validation("dateOfBirth", value)}
                  data-cy="DATE-OF-BIRTH-INPUT"
                />
                <NativeSelect
                  name="gender"
                  label="Gender"
                  disabled={profileData?.kycStatus === "VERIFIED"}
                  size="small"
                  data-cy="GENDER-INPUT"
                  value={formData?.gender}
                  options={["", "male", "female", "others", "unknown"]}
                />
              </div>

              <InputBox
                labelClassName="letter-spacing-4-percentage"
                className="inherit-parent-width border-radius-default"
                label="Email"
                type="email"
                name="email"
                value={formData?.email}
                validation={(value) => validation("email", value)}
                data-cy="EMAIL-INPUT"
                // disabled={profileData?.kycStatus === "VERIFIED"}
              />

              <TextArea
                type="text"
                className="padding-top-default font-color-secondary"
                value={formData?.address}
                name="address"
                label="Address"
                data-cy="ADDRESS-INPUT"
                required
                category="textarea"
                validation={(value) => validation("address", value)}
              />

              <InputBox
                labelClassName="letter-spacing-4-percentage"
                className="inherit-parent-width border-radius-default padding-top-default"
                label="PIN Code"
                type="tel"
                required
                name="pinCode"
                value={formData?.pinCode}
                validation={(value) => validation("pinCode", value)}
                data-cy="PIN-CODE-INPUT"
              />

              <div className="display-flex flex-center-children-vertically padding-top-default">
                <NativeSelect
                  labelClassName="letter-spacing-4-percentage"
                  className="margin-right-default"
                  selectClassName="text-transform-capitalize padding-top-large"
                  optionsClassName="text-transform-capitalize"
                  name="stateCode"
                  label="State"
                  required
                  size="half"
                  data-cy="STATE-CODE-INPUT"
                  value={formData?.stateCode}
                  options={stateOptionsWithCode}
                />
                <NativeSelect
                  labelClassName="letter-spacing-4-percentage"
                  selectClassName="text-transform-capitalize  padding-top-large"
                  optionsClassName="text-transform-capitalize"
                  name="districtCode"
                  label="City"
                  required
                  size="half"
                  data-cy="DISTRICT-CODE-INPUT"
                  value={formData?.districtCode}
                  options={stateCodeWithDistricts[formData?.stateCode]}
                />
              </div>
            </div>
            <div>
              <Button
                text="Save changes"
                data-cy="SAVE-CHANGES-BUTTON"
                boxShadow={false}
                className="margin-top-large"
                required
                type="submit"
                disabled={!handleDisable()}
                loading={props.patients.loading}
              />
            </div>
          </form>
        )}
      </section>
      <div onSubmit={(e) => e.stopPropagation()}></div>
      <Routes>
        <Route
          path="cropper"
          element={
            <Modal
              show={true}
              canIgnore={true}
              onClose={() => {
                setFile(null);
                navigate("/settings/editProfile");
              }}
              width="inherit-parent-width"
              maxWidth="max-width-800px"
              background="false"
              boxShadow="false"
              borderRadius="false"
              height="height-90-percent"
            >
              <div
                data-cy="CROPPER-MODAL"
                className="background-white inherit-parent-height border-radius-default box-shadow-default"
              >
                <CropperModal
                  className="border-radius-default"
                  aspectRatio={1}
                  OnBlobCreate={(blob) => {
                    const reader = new FileReader();
                    reader.readAsDataURL(blob);
                    reader.onload = () => {
                      const updatedData = {
                        ...formData,
                        profilePhoto: reader.result
                      };
                      setFormData(updatedData);
                      editProfile(editProfileSchema(updatedData));
                      setFile(null);
                      navigate("/settings/editProfile");
                    };
                  }}
                  file={file}
                  imageFormate="jpeg"
                  exportSettings={{ height: 128, width: 128 }}
                  setFile={() => {
                    setFile(null);
                    navigate(-1);
                  }}
                />
              </div>
            </Modal>
          }
        />

        <Route
          path="verify"
          element={
            <VerifyModal
              resendCount={resendCount}
              setResendCount={setResendCount}
              mode={mode}
              counter={counter}
              setCounter={setCounter}
              contactValue={contactValue}
              patients={props.patients}
            />
          }
        />

        <Route
          path="confirmLogin"
          element={
            <ConfirmLoginModal patients={props.patients} auth={props.auth} />
          }
        />
      </Routes>
      <div />
    </>
  );
};

const mapStateToProps = (state) => ({
  error: state.status,
  patients: state.patients,
  auth: state.auth,
  app: state.app
});

export default connect(mapStateToProps)(EditProfile);

const VerifyModal = (props) => {
  const [OTP, setOTP] = useState(null);
  const navigate = useNavigate();

  return (
    <Modal
      show={true}
      onClose={() => {
        setTransactionId(null);
      }}
      canIgnore={false}
      position="position-fixed bottom-0 left-0 right-0"
      boxShadow="none"
      borderRadius="none"
      width="inherit-parent-width"
      background="background-transparent"
      height="height-fit-to-content"
    >
      <footer className="inherit-parent-width background-white">
        <div className="padding-larger">
          <div className="flex-justify-content-space-between">
            <div>
              <BackArrowIcon />
            </div>
            <div className="text-align-center padding-bottom-default font-size-smaller">
              {props.mode === "phoneNumber"
                ? "CHANGE MOBILE NUMBER?"
                : "CHANGE EMAIL ADDRESS?"}
            </div>
            <div />
          </div>

          <form
            onSubmit={(event) => {
              event.preventDefault();
              verifyOtpForUpdate(
                OTP,
                props.mode === "phoneNumber" ? "Phone" : "Email",
                props.contactValue,
                navigate
              );
            }}
          >
            <OTPInputBox
              name="otp"
              autoFocus
              value={OTP}
              setValue={setOTP}
              label={`Enter the OTP sent to ${
                props.mode === "phoneNumber"
                  ? props.contactValue
                  : props.contactValue?.toLowerCase()
              }`}
              required
              className="margin-bottom-large"
            />

            <div className="display-flex flex-justify-content-space-between font-size-medium margin-bottom-larger">
              {props.counter &&
              typeof props.counter === "number" &&
              props.resendCount <= 1 ? (
                <div>
                  {`00:${props.counter.toString().padStart(2, "0")} seconds`}
                </div>
              ) : (
                <div />
              )}
              <div
                className={
                  typeof props.counter === "number" && props.counter > 0
                    ? "font-color-tertiary"
                    : "font-color-primary"
                }
                onClick={() => {
                  const isTimerActive =
                    typeof props.counter === "number" &&
                    props.counter > 0 &&
                    props.resendCount <= 1;
                  // Disable only while timer is running
                  if (isTimerActive) {
                    return;
                  }
                  // 2. Throw error after 2 attempts
                  if (props.resendCount >= 2) {
                    return setErrorStatus({
                      code: "custom",
                      message: "OTP Resend limit reached. Try after 30 minutes."
                    });
                  }

                  generateOtpForUpdate(
                    props.contactValue,
                    props.mode === "phoneNumber" ? "Phone" : "Email"
                  );
                  const nextCount = props.resendCount + 1;

                  props.setResendCount(nextCount);

                  if (nextCount === 1) {
                    props.setCounter(60);
                  } else {
                    // second resend onwards
                    props.setCounter(null);
                  }
                }}
              >
                Resend OTP
              </div>
            </div>

            <Button
              text="Continue"
              data-cy="CONTINUE-BUTTON"
              boxShadow={false}
              type="submit"
              disabled={OTP?.length !== 6}
              loading={props.patients.loading}
            />
          </form>
        </div>
      </footer>
    </Modal>
  );
};

const ConfirmLoginModal = (props) => {
  const navigate = useNavigate();

  return (
    <Modal
      show={true}
      onClose={() => {
        setTransactionId(null);
        navigate("/settings/editProfile");
      }}
      canIgnore={false}
      position="position-fixed bottom-0 left-0 right-0"
      boxShadow="none"
      borderRadius="none"
      width="inherit-parent-width"
      background="background-transparent"
      height="height-fit-to-content"
    >
      <footer className="inherit-parent-width background-white">
        <div className="padding-larger ">
          <div className="display-flex flex-justify-content-space-between">
            <div>
              <BackArrowIcon />
            </div>
            <div className="text-align-center font-family-gilroy-regular font-size-small">
              MOBILE NUMBER CHANGED
            </div>
            <div />
          </div>
          <div className="text-align-center padding-bottom-default font-size-smaller">
            You will be logged out after this change.
          </div>

          <div className="display-flex padding-top-larger">
            <Button
              text="Confirm"
              boxShadow={false}
              required
              type="button"
              className={"margin-top-default margin-right-default"}
              data-cy={"create-new-demographic-button"}
              loading={props.auth.loading}
              onClick={() => {
                logout(true);
              }}
            />
          </div>
        </div>
      </footer>
    </Modal>
  );
};
